import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMapsLoader } from './GoogleMapsLoaderProvider';
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  Close as CloseIcon,
  List as ListIcon,
  Map as MapIcon,
  Remove as RemoveIcon,
  SatelliteAlt as SatelliteIcon,
  OpenInFull as OpenInFullIcon,
} from '@mui/icons-material';
import MapSelectedProgramPanel from '../map/MapSelectedProgramPanel';
import MapSidePanelFilters from '../map/MapSidePanelFilters';
import {
  createEmptyMapPanelFilters,
  filterPropertiesForMap,
  type MapPanelFilters,
} from '../../utils/filterMapProperties';
import { MapProgramLotsCard, ProjectCard } from '../sections';
import { Property } from '../../types';
import { defaultMapCenter, defaultMapZoom } from '../../config/mapConfig';
import {
  buildProgramInfo,
  buildProjectDetailPayload,
  groupPropertiesByProgram,
  programGroupToProjectCard,
  resolveProgramKeyFromCatalog,
  type PropertyProgramGroup,
} from '../../utils/propertyToProjectCard';

type PanelViewMode = 'programme' | 'lots';

const MAP_SIDE_PANEL_WIDTH_PROGRAMME = 420;
const MAP_SIDE_PANEL_WIDTH_LOTS = 520;

interface MapWidgetProps {
  /** Biens filtrés sur la page (liste catalogue) — non utilisés pour l’ouverture carte */
  properties: Property[];
  /**
   * Catalogue complet affiché sur la carte à chaque ouverture,
   * indépendamment des filtres actifs sur la page.
   */
  allProperties?: Property[];
  /** Programme à mettre en avant à l’ouverture (clé programme ou id lot). */
  initialProgramKey?: string;
  /** Afficher le panneau liste à gauche à l’ouverture (défaut : true). */
  openListOnLaunch?: boolean;
  /** Masquer « Voir fiche » pour ce programme (ex. page détail en cours). */
  hideViewSheetForProgramKey?: string;
  className?: string;
  /** Libellé du bouton d’ouverture. */
  triggerLabel?: string;
  /** `floating` | `inline` | `overlay` | `embed` (carte inline + plein écran). */
  placement?: 'floating' | 'inline' | 'overlay' | 'embed';
  /** Adresse / ville pour centrer l’aperçu embarqué (`placement="embed"`). */
  embedLocationQuery?: string;
  /** Centre GPS direct (parcelle / carte prospection) — prioritaire sur le géocodage. */
  embedCenter?: { lat: number; lng: number };
}

interface PropertyMarker {
  propertyId: string;
  programKey?: string;
  lat: number;
  lng: number;
}

const isPropertyMarker = (marker: PropertyMarker | null): marker is PropertyMarker =>
  marker !== null;

const MIN_ZOOM = 5;
const MAX_ZOOM = 20;
const DEFAULT_ZOOM = 14;

/** `hybrid` = imagerie satellite + noms de rues (vue détaillée type carte réelle). */
type SiteMapTypeId = 'roadmap' | 'hybrid';

const DEFAULT_MAP_TYPE: SiteMapTypeId = 'hybrid';

const ROADMAP_STYLES: google.maps.MapTypeStyle[] = [
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
];

const getMapOptions = (mapTypeId: SiteMapTypeId): google.maps.MapOptions => ({
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  scaleControl: false,
  rotateControl: false,
  scrollwheel: true,
  gestureHandling: 'greedy',
  mapTypeId,
  styles: mapTypeId === 'roadmap' ? ROADMAP_STYLES : undefined,
});

const getInitialCenter = (properties: Property[]): google.maps.LatLngLiteral => {
  const withCoords = properties.filter((p) => p.location.coordinates);
  if (withCoords.length === 0) return defaultMapCenter;

  const sum = withCoords.reduce(
    (acc, p) => {
      const { lat, lng } = p.location.coordinates!;
      return { lat: acc.lat + lat, lng: acc.lng + lng };
    },
    { lat: 0, lng: 0 }
  );

  return {
    lat: sum.lat / withCoords.length,
    lng: sum.lng / withCoords.length,
  };
};

const geocodeProperty = (
  geocoder: google.maps.Geocoder,
  property: Property
): Promise<{ lat: number; lng: number } | null> =>
  new Promise((resolve) => {
    if (property.location.coordinates) {
      const { lat, lng } = property.location.coordinates;
      resolve({ lat, lng });
      return;
    }

    const { address, city, postalCode } = property.location;
    const query = `${address}, ${postalCode} ${city}, France`;

    geocoder.geocode({ address: query, region: 'fr' }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng() });
      } else {
        resolve(null);
      }
    });
  });

const MapWidgetContent: React.FC<MapWidgetProps & { onEnableMaps: () => void }> = ({
  properties,
  allProperties,
  initialProgramKey,
  openListOnLaunch = true,
  hideViewSheetForProgramKey,
  className = '',
  triggerLabel = 'Afficher la carte',
  placement = 'floating',
  embedLocationQuery = '',
  embedCenter: embedCenterProp,
  onEnableMaps,
}) => {
  const mapCatalog = useMemo(
    () => allProperties ?? properties,
    [allProperties, properties]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [showList, setShowList] = useState(false);
  const [mapCatalogSource, setMapCatalogSource] = useState<Property[]>(mapCatalog);
  const [mapFilters, setMapFilters] = useState<MapPanelFilters>(createEmptyMapPanelFilters);
  const [displayZoom, setDisplayZoom] = useState(DEFAULT_ZOOM);
  const [markers, setMarkers] = useState<PropertyMarker[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [selectedProgramKey, setSelectedProgramKey] = useState<string | null>(null);
  const [panelViewMode, setPanelViewMode] = useState<PanelViewMode>('programme');
  const [mapTypeId, setMapTypeId] = useState<SiteMapTypeId>(DEFAULT_MAP_TYPE);
  const [embedCenter, setEmbedCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const [embedZoom, setEmbedZoom] = useState(15);
  const [isEmbedGeocoding, setIsEmbedGeocoding] = useState(false);

  const filteredMapProperties = useMemo(
    () => filterPropertiesForMap(mapCatalogSource, mapFilters),
    [mapCatalogSource, mapFilters]
  );

  const programGroups = useMemo<PropertyProgramGroup[]>(
    () => groupPropertiesByProgram(filteredMapProperties),
    [filteredMapProperties]
  );

  const totalProgramGroupsCount = useMemo(
    () => groupPropertiesByProgram(mapCatalogSource).length,
    [mapCatalogSource]
  );

  const scrollYRef = useRef(0);
  const mapRef = useRef<google.maps.Map | null>(null);
  const embedMapRef = useRef<google.maps.Map | null>(null);
  const pendingFocusProgramKeyRef = useRef<string | null>(null);
  const navigate = useNavigate();

  const selectedProgramGroup = useMemo(
    () =>
      selectedProgramKey
        ? programGroups.find((g) => g.key === selectedProgramKey) ?? null
        : null,
    [programGroups, selectedProgramKey]
  );

  const selectedProgramIndex = useMemo(
    () =>
      selectedProgramKey
        ? programGroups.findIndex((g) => g.key === selectedProgramKey)
        : -1,
    [programGroups, selectedProgramKey]
  );

  const embedContainerRef = useRef<HTMLDivElement>(null);

  const { isLoaded, loadError } = useGoogleMapsLoader();

  const closeMap = useCallback(() => {
    setIsOpen(false);
    setShowList(false);
    setDisplayZoom(DEFAULT_ZOOM);
    setMarkers([]);
    setSelectedPropertyId(null);
    setSelectedProgramKey(null);
    setPanelViewMode('programme');
    setMapTypeId(DEFAULT_MAP_TYPE);
    setMapFilters(createEmptyMapPanelFilters());
  }, []);

  const openMap = useCallback(() => {
    onEnableMaps();
    const catalog = allProperties ?? properties;
    const groups = groupPropertiesByProgram(catalog);
    const resolvedFocus = initialProgramKey
      ? resolveProgramKeyFromCatalog(catalog, initialProgramKey)
      : null;

    setMapCatalogSource(catalog);
    setMapFilters(createEmptyMapPanelFilters());
    setMarkers([]);
    setSelectedPropertyId(null);
    setSelectedProgramKey(resolvedFocus);
    pendingFocusProgramKeyRef.current = resolvedFocus;
    setShowList(openListOnLaunch && groups.length > 0);
    setDisplayZoom(DEFAULT_ZOOM);
    setIsOpen(true);
  }, [allProperties, initialProgramKey, onEnableMaps, openListOnLaunch, properties]);

  const applyProgramFocus = useCallback(
    (programKey: string, markerList: PropertyMarker[]) => {
      const marker = markerList.find((m) => m.programKey === programKey);
      if (!marker) return false;

      setSelectedProgramKey(programKey);
      setSelectedPropertyId(null);

      const map = mapRef.current;
      if (map) {
        map.panTo({ lat: marker.lat, lng: marker.lng });
        const nextZoom = Math.max(map.getZoom() ?? DEFAULT_ZOOM, 15);
        map.setZoom(nextZoom);
        setDisplayZoom(nextZoom);
      }
      return true;
    },
    []
  );

  const panMapTo = useCallback((lat: number, lng: number) => {
    const map = mapRef.current;
    if (!map) return;
    map.panTo({ lat, lng });
    const nextZoom = Math.max(map.getZoom() ?? DEFAULT_ZOOM, 15);
    map.setZoom(nextZoom);
    setDisplayZoom(nextZoom);
  }, []);

  const focusPropertyOnMap = useCallback(
    (propertyId: string) => {
      const marker = markers.find((m) => m.propertyId === propertyId);
      if (!marker) return;
      const group = programGroups.find((g) =>
        g.lots.some((lot) => lot.id === propertyId)
      );
      setSelectedPropertyId(propertyId);
      setSelectedProgramKey(group?.key ?? null);
      panMapTo(marker.lat, marker.lng);
    },
    [markers, panMapTo, programGroups]
  );

  const focusProgramOnMap = useCallback(
    (programKey: string) => {
      const marker = markers.find((m) => m.programKey === programKey);
      if (!marker) return;
      setSelectedProgramKey(programKey);
      setSelectedPropertyId(null);
      panMapTo(marker.lat, marker.lng);
    },
    [markers, panMapTo]
  );

  const clearProgramSelection = useCallback(() => {
    setSelectedProgramKey(null);
    setSelectedPropertyId(null);
  }, []);

  const selectedProgramProject = useMemo(() => {
    if (!selectedProgramGroup || selectedProgramIndex < 0) return null;
    return programGroupToProjectCard(selectedProgramGroup, selectedProgramIndex);
  }, [selectedProgramGroup, selectedProgramIndex]);

  const goToSelectedProgramPage = useCallback(() => {
    if (!selectedProgramGroup || selectedProgramIndex < 0) return;

    const project = programGroupToProjectCard(
      selectedProgramGroup,
      selectedProgramIndex
    );
    sessionStorage.setItem(
      'projectData',
      JSON.stringify(
        buildProjectDetailPayload(selectedProgramGroup.representative, project)
      )
    );
    closeMap();
    navigate(`/projet/${project.id}`);
  }, [closeMap, navigate, selectedProgramGroup, selectedProgramIndex]);

  const toggleMapView = useCallback(() => {
    const next: SiteMapTypeId = mapTypeId === 'roadmap' ? 'hybrid' : 'roadmap';
    setMapTypeId(next);
    mapRef.current?.setMapTypeId(next);
  }, [mapTypeId]);

  const adjustZoom = useCallback((delta: number) => {
    const map = mapRef.current;
    if (!map) return;
    const next = Math.min(
      MAX_ZOOM,
      Math.max(MIN_ZOOM, (map.getZoom() ?? DEFAULT_ZOOM) + delta)
    );
    map.setZoom(next);
    setDisplayZoom(next);
  }, []);

  const adjustEmbedZoom = useCallback((delta: number) => {
    const map = embedMapRef.current;
    if (!map) return;
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, (map.getZoom() ?? embedZoom) + delta));
    map.setZoom(next);
    setEmbedZoom(next);
  }, [embedZoom]);

  const embedFocusedGroup = useMemo(() => {
    if (placement !== 'embed') return null;
    const catalog = allProperties ?? properties;
    if (catalog.length === 0) return null;
    const resolvedKey = initialProgramKey
      ? resolveProgramKeyFromCatalog(catalog, initialProgramKey)
      : null;
    if (!resolvedKey) return null;
    return groupPropertiesByProgram(catalog).find((group) => group.key === resolvedKey) ?? null;
  }, [allProperties, initialProgramKey, placement, properties]);

  useEffect(() => {
    if (placement !== 'embed') return undefined;

    const node = embedContainerRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) onEnableMaps();
      },
      { rootMargin: '400px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [onEnableMaps, placement]);

  useEffect(() => {
    if (placement !== 'embed') return undefined;

    if (
      embedCenterProp &&
      Number.isFinite(embedCenterProp.lat) &&
      Number.isFinite(embedCenterProp.lng)
    ) {
      setEmbedCenter({ lat: embedCenterProp.lat, lng: embedCenterProp.lng });
      setIsEmbedGeocoding(false);
      return undefined;
    }

    if (embedFocusedGroup?.representative.location.coordinates) {
      const { lat, lng } = embedFocusedGroup.representative.location.coordinates;
      setEmbedCenter({ lat, lng });
      setIsEmbedGeocoding(false);
      return undefined;
    }

    const query =
      embedLocationQuery.trim() ||
      (embedFocusedGroup
        ? `${embedFocusedGroup.representative.location.address}, ${embedFocusedGroup.representative.location.postalCode} ${embedFocusedGroup.representative.location.city}`
        : '');

    if (!query) {
      setEmbedCenter(defaultMapCenter);
      setIsEmbedGeocoding(false);
      return undefined;
    }

    if (!isLoaded || !window.google?.maps) {
      setIsEmbedGeocoding(true);
      return undefined;
    }

    let cancelled = false;
    setIsEmbedGeocoding(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: `${query}, France`, region: 'fr' }, (results, status) => {
      if (cancelled) return;
      setIsEmbedGeocoding(false);
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        setEmbedCenter({ lat: loc.lat(), lng: loc.lng() });
      } else {
        setEmbedCenter(defaultMapCenter);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    embedCenterProp,
    embedFocusedGroup,
    embedLocationQuery,
    isLoaded,
    placement,
  ]);

  const onEmbedMapLoad = useCallback((map: google.maps.Map) => {
    embedMapRef.current = map;
    map.setMapTypeId(DEFAULT_MAP_TYPE);
    if (embedCenter) {
      map.setCenter(embedCenter);
      map.setZoom(embedZoom);
    }
  }, [embedCenter, embedZoom]);

  const onEmbedMapUnmount = useCallback(() => {
    embedMapRef.current = null;
  }, []);

  useEffect(() => {
    if (!embedMapRef.current || !embedCenter) return;
    embedMapRef.current.setCenter(embedCenter);
  }, [embedCenter]);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      const center = getInitialCenter(filteredMapProperties);
      map.setCenter(center);
      map.setZoom(programGroups.length <= 1 ? DEFAULT_ZOOM : defaultMapZoom);
      setDisplayZoom(map.getZoom() ?? DEFAULT_ZOOM);

      map.setMapTypeId(DEFAULT_MAP_TYPE);
      setMapTypeId(DEFAULT_MAP_TYPE);

      map.addListener('zoom_changed', () => {
        setDisplayZoom(map.getZoom() ?? DEFAULT_ZOOM);
      });
    },
    [filteredMapProperties, programGroups.length]
  );

  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  useEffect(() => {
    if (!isOpen || !isLoaded || !window.google?.maps) return undefined;

    if (filteredMapProperties.length === 0) {
      setMarkers([]);
      setIsGeocoding(false);
      return undefined;
    }

    let cancelled = false;
    setIsGeocoding(true);

    const geocoder = new window.google.maps.Geocoder();

    const loadMarkers = async (): Promise<PropertyMarker[]> => {
      if (panelViewMode === 'programme') {
        const results = await Promise.all(
          programGroups.map(async (group): Promise<PropertyMarker | null> => {
            const position = await geocodeProperty(geocoder, group.representative);
            if (!position) return null;
            return {
              programKey: group.key,
              propertyId: group.representative.id,
              lat: position.lat,
              lng: position.lng,
            };
          })
        );
        return results.filter(isPropertyMarker);
      }

      const results = await Promise.all(
        filteredMapProperties.map(async (property): Promise<PropertyMarker | null> => {
          const position = await geocodeProperty(geocoder, property);
          if (!position) return null;
          return {
            propertyId: property.id,
            lat: position.lat,
            lng: position.lng,
          };
        })
      );
      return results.filter(isPropertyMarker);
    };

    loadMarkers().then((valid) => {
      if (cancelled) return;

      setMarkers(valid);
      setIsGeocoding(false);

      const map = mapRef.current;
      if (!map || valid.length === 0) return;

      const pendingFocus = pendingFocusProgramKeyRef.current;
      if (pendingFocus && applyProgramFocus(pendingFocus, valid)) {
        pendingFocusProgramKeyRef.current = null;
        return;
      }

      if (valid.length === 1) {
        map.setCenter({ lat: valid[0].lat, lng: valid[0].lng });
        map.setZoom(DEFAULT_ZOOM);
        setDisplayZoom(DEFAULT_ZOOM);
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      valid.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
      const panelWidth =
        panelViewMode === 'lots'
          ? MAP_SIDE_PANEL_WIDTH_LOTS
          : MAP_SIDE_PANEL_WIDTH_PROGRAMME;
      const leftPadding =
        programGroups.length > 0 && showList ? panelWidth + 24 : 56;
      map.fitBounds(bounds, { top: 56, right: 56, bottom: 56, left: leftPadding });
      const z = map.getZoom();
      if (z != null) setDisplayZoom(z);
    });

    return () => {
      cancelled = true;
    };
  }, [
    applyProgramFocus,
    isOpen,
    isLoaded,
    filteredMapProperties,
    programGroups,
    panelViewMode,
    showList,
  ]);

  useEffect(() => {
    if (!selectedProgramKey) return;
    if (!programGroups.some((group) => group.key === selectedProgramKey)) {
      setSelectedProgramKey(null);
      setSelectedPropertyId(null);
    }
  }, [programGroups, selectedProgramKey]);

  useEffect(() => {
    if (!isOpen || !mapRef.current || !window.google?.maps) return undefined;
    const timer = window.setTimeout(() => {
      window.google.maps.event.trigger(mapRef.current!, 'resize');
    }, 280);
    return () => window.clearTimeout(timer);
  }, [isOpen, showList, panelViewMode]);

  useEffect(() => {
    if (!isOpen) return undefined;

    scrollYRef.current = window.scrollY;
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showList) {
          setShowList(false);
        } else {
          closeMap();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      html.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollYRef.current);
      });
    };
  }, [isOpen, closeMap, showList]);

  const triggerButton = (
    <button
      type="button"
      onClick={openMap}
      className={
        placement === 'overlay'
          ? `inline-flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white shadow-lg ring-2 ring-white/90 transition-colors hover:bg-primary-600 ${className}`
          : placement === 'inline'
            ? className.includes('catalog-map-btn')
              ? `inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-2 text-sm font-medium transition-[background-color,box-shadow,transform,border-color] duration-200 group sm:px-6 ${className}`
              : `inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-primary-500 px-5 py-2 text-sm font-medium text-white shadow-sm transition-[background-color,box-shadow,transform] duration-200 hover:bg-primary-600 hover:shadow-md group sm:px-6 ${className}`
            : `flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-6 py-3 text-white shadow-soft-lg transition-all duration-300 hover:bg-primary-600 hover:shadow-soft-md group ${className}`
      }
      aria-label={triggerLabel}
      title={triggerLabel}
    >
      <MapIcon className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
      <span className="font-medium text-sm">{triggerLabel}</span>
    </button>
  );

  const floatingButton = (
    <div className={`fixed bottom-4 left-1/2 z-[85] -translate-x-1/2 ${className}`}>
      {triggerButton}
    </div>
  );

  const embedFullscreenButton = (
    <button
      type="button"
      onClick={openMap}
      className="flex h-10 w-10 items-center justify-center bg-white text-neutral-700 transition-colors hover:bg-neutral-50"
      aria-label="Ouvrir la carte en plein écran"
      title="Plein écran"
    >
      <OpenInFullIcon className="h-5 w-5" />
    </button>
  );

  const embedMapBlock =
    placement === 'embed' ? (
      <div
        ref={embedContainerRef}
        className={`relative w-full min-h-[24rem] overflow-hidden bg-neutral-100 ${className}`}
      >
        {loadError ? (
          <div className="flex min-h-96 items-center justify-center px-4 text-center text-sm text-neutral-600">
            Impossible de charger la carte. Vérifiez la clé Google Maps.
          </div>
        ) : !isLoaded || isEmbedGeocoding || !embedCenter ? (
          <div className="flex min-h-96 items-center justify-center">
            <div
              className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary-200 border-t-primary-500"
              role="status"
              aria-label="Chargement de la carte"
            />
          </div>
        ) : (
          <GoogleMap
            mapContainerClassName="absolute inset-0 h-full w-full"
            center={embedCenter}
            zoom={embedZoom}
            onLoad={onEmbedMapLoad}
            onUnmount={onEmbedMapUnmount}
            mapTypeId={DEFAULT_MAP_TYPE}
            options={{
              ...getMapOptions(DEFAULT_MAP_TYPE),
              gestureHandling: 'cooperative',
              scrollwheel: false,
            }}
          >
            <Marker position={embedCenter} />
          </GoogleMap>
        )}

        {isLoaded && embedCenter && !loadError && (
          <div className="absolute bottom-3 right-3 z-10 flex flex-col overflow-hidden rounded-sm border border-neutral-200/90 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
            <button
              type="button"
              onClick={() => adjustEmbedZoom(1)}
              disabled={embedZoom >= MAX_ZOOM}
              className="flex h-10 w-10 items-center justify-center text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-40"
              aria-label="Zoom avant"
            >
              <AddIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => adjustEmbedZoom(-1)}
              disabled={embedZoom <= MIN_ZOOM}
              className="flex h-10 w-10 items-center justify-center border-t border-neutral-200 text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-40"
              aria-label="Zoom arrière"
            >
              <RemoveIcon className="h-5 w-5" />
            </button>
            <div className="border-t border-neutral-200">{embedFullscreenButton}</div>
          </div>
        )}
      </div>
    ) : null;

  const lotsCount = filteredMapProperties.length;
  const programsCount = programGroups.length;
  const panelCountLabel =
    panelViewMode === 'lots'
      ? `${lotsCount} lot${lotsCount > 1 ? 's' : ''} · ${programsCount} programme${programsCount > 1 ? 's' : ''}`
      : programsCount !== lotsCount
        ? `${programsCount} programme${programsCount > 1 ? 's' : ''} · ${lotsCount} lot${lotsCount > 1 ? 's' : ''}`
        : `${programsCount} programme${programsCount > 1 ? 's' : ''} sur la carte`;

  const mapSurface = () => {
    if (loadError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 bg-neutral-100 px-6 text-center">
          <p className="text-sm font-medium text-neutral-800">Impossible de charger Google Maps</p>
          <p className="text-xs text-neutral-600">
            Vérifiez REACT_APP_GOOGLE_MAPS_API_KEY dans le fichier .env
          </p>
        </div>
      );
    }

    if (!isLoaded) {
      return (
        <div className="flex h-full items-center justify-center bg-neutral-100">
          <div className="flex flex-col items-center gap-3">
            <div
              className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary-200 border-t-primary-500"
              role="status"
              aria-label="Chargement de la carte"
            />
            <p className="text-sm text-neutral-600">Chargement de la carte…</p>
          </div>
        </div>
      );
    }

    return (
      <GoogleMap
        mapContainerClassName="h-full w-full"
        center={getInitialCenter(filteredMapProperties)}
        zoom={programGroups.length <= 1 ? DEFAULT_ZOOM : defaultMapZoom}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        mapTypeId={mapTypeId}
        options={getMapOptions(mapTypeId)}
      >
        {markers.map((marker) => {
          const property = filteredMapProperties.find((p) => p.id === marker.propertyId);
          const programKey = marker.programKey;
          const group = programKey
            ? programGroups.find((g) => g.key === programKey)
            : undefined;
          const title =
            property?.title ?? (group ? buildProgramInfo(group).name : 'Programme');
          const markerKey = programKey ?? marker.propertyId;
          return (
            <Marker
              key={markerKey}
              position={{ lat: marker.lat, lng: marker.lng }}
              title={title}
              onClick={() => {
                if (panelViewMode === 'programme' && programKey) {
                  focusProgramOnMap(programKey);
                } else {
                  focusPropertyOnMap(marker.propertyId);
                }
              }}
            />
          );
        })}
      </GoogleMap>
    );
  };

  const fullscreenOverlay =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed inset-0 z-[10050] flex bg-neutral-100"
            role="dialog"
            aria-modal="true"
            aria-label="Carte des programmes en plein écran"
          >
            {showList && (
              <aside
                className={`flex h-full shrink-0 flex-col border-r border-neutral-200 bg-neutral-50 shadow-[4px_0_24px_rgba(0,0,0,0.06)] ${
                  panelViewMode === 'lots'
                    ? 'w-[min(96vw,520px)] sm:w-[520px]'
                    : 'w-[min(92vw,420px)] sm:w-[420px]'
                }`}
                aria-label="Liste des biens sur la carte"
              >
                <div className="shrink-0 border-b border-neutral-200 bg-white px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-neutral-900">Résultats</h3>
                      <p className="mt-0.5 text-xs text-neutral-500">{panelCountLabel}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowList(false)}
                      className="shrink-0 rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-100"
                      aria-label="Masquer le panneau"
                      title="Masquer le panneau"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="mt-3">
                    <p className="mb-1.5 text-[11px] font-medium text-neutral-600">Vue par :</p>
                    <div
                      className="flex rounded-lg border border-neutral-200 bg-neutral-100 p-1"
                      role="group"
                      aria-label="Mode d'affichage du panneau"
                    >
                      <button
                        type="button"
                        onClick={() => setPanelViewMode('programme')}
                        className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-[color,background-color] sm:text-sm ${
                          panelViewMode === 'programme'
                            ? 'bg-primary-500 text-white shadow-sm'
                            : 'text-neutral-700 hover:text-neutral-900'
                        }`}
                      >
                        Programmes
                      </button>
                      <button
                        type="button"
                        onClick={() => setPanelViewMode('lots')}
                        className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-[color,background-color] sm:text-sm ${
                          panelViewMode === 'lots'
                            ? 'bg-primary-500 text-white shadow-sm'
                            : 'text-neutral-700 hover:text-neutral-900'
                        }`}
                      >
                        Lots
                      </button>
                    </div>
                  </div>

                  <MapSidePanelFilters
                    filters={mapFilters}
                    onChange={setMapFilters}
                    totalPrograms={totalProgramGroupsCount}
                    filteredPrograms={programsCount}
                  />
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
                  {programGroups.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-neutral-200 bg-white px-4 py-8 text-center">
                      <p className="text-sm font-medium text-neutral-800">
                        Aucun programme ne correspond
                      </p>
                      <p className="mt-1 text-xs text-neutral-500">
                        Modifiez ou effacez les filtres pour élargir la recherche.
                      </p>
                      <button
                        type="button"
                        onClick={() => setMapFilters(createEmptyMapPanelFilters())}
                        className="mt-3 text-sm font-semibold text-primary-600 hover:text-primary-700"
                      >
                        Effacer les filtres
                      </button>
                    </div>
                  ) : panelViewMode === 'programme' ? (
                    <ul className="flex flex-col gap-5">
                      {programGroups.map((group, index) => {
                        const project = programGroupToProjectCard(group, index);
                        return (
                          <li key={group.key}>
                            <ProjectCard
                              project={project}
                              className={`w-full shadow-[0_4px_20px_-4px_rgba(43,47,51,0.12)] ${
                                selectedProgramKey === group.key
                                  ? 'ring-2 ring-primary-500 ring-offset-2'
                                  : ''
                              }`}
                              onClick={() => focusProgramOnMap(group.key)}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <ul className="flex flex-col gap-5">
                      {programGroups.map((group) => {
                        const program = buildProgramInfo(group);
                        return (
                          <li key={group.key}>
                            <MapProgramLotsCard
                              program={program}
                              lots={group.lots}
                              isProgramHighlighted={selectedProgramKey === group.key}
                              selectedLotId={selectedPropertyId}
                              onLotClick={(lot) => focusPropertyOnMap(lot.id)}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </aside>
            )}

            <div className="relative min-h-0 min-w-0 flex-1">
              {mapSurface()}

              {isGeocoding && isLoaded && (
                <div className="pointer-events-none absolute inset-x-0 top-16 z-[5] flex justify-center">
                  <span className="rounded-lg bg-white/95 px-3 py-1.5 text-xs text-neutral-600 shadow-md ring-1 ring-black/5">
                    Placement des programmes…
                  </span>
                </div>
              )}

              {!showList && programGroups.length > 0 && (
                <div className="absolute left-3 top-3 z-10 sm:left-4 sm:top-4">
                  <span className="inline-flex items-center rounded-lg bg-white/95 px-3 py-1.5 text-xs font-medium text-neutral-800 shadow-md ring-1 ring-black/5 backdrop-blur-sm sm:text-sm">
                    {panelCountLabel}
                  </span>
                </div>
              )}

              <button
                type="button"
                onClick={closeMap}
                className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-neutral-800 shadow-lg ring-1 ring-black/10 backdrop-blur-sm transition-colors hover:bg-white sm:right-4 sm:top-4"
                aria-label="Fermer la carte"
              >
                <CloseIcon className="h-6 w-6" />
              </button>

              <div className="absolute right-3 top-[4.25rem] z-10 flex flex-col overflow-hidden rounded-lg bg-white/95 shadow-lg ring-1 ring-black/10 backdrop-blur-sm sm:right-4 sm:top-[4.75rem]">
                <button
                  type="button"
                  onClick={() => adjustZoom(1)}
                  disabled={displayZoom >= MAX_ZOOM}
                  className="flex h-10 w-10 items-center justify-center text-neutral-800 transition-colors hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Zoom avant"
                >
                  <AddIcon className="h-5 w-5" />
                </button>
                <div className="border-t border-neutral-200 px-1 py-0.5 text-center text-[10px] font-semibold tabular-nums text-neutral-500">
                  {displayZoom}
                </div>
                <button
                  type="button"
                  onClick={() => adjustZoom(-1)}
                  disabled={displayZoom <= MIN_ZOOM}
                  className="flex h-10 w-10 items-center justify-center text-neutral-800 transition-colors hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Zoom arrière"
                >
                  <RemoveIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={toggleMapView}
                  className={`flex h-10 w-10 flex-col items-center justify-center gap-0.5 border-t border-neutral-200 text-neutral-800 transition-colors hover:bg-neutral-100 ${
                    mapTypeId === DEFAULT_MAP_TYPE ? 'bg-primary-50 text-primary-700' : ''
                  }`}
                  aria-label={
                    mapTypeId === 'roadmap'
                      ? 'Passer en vue satellite détaillée'
                      : 'Passer en vue plan'
                  }
                  title={
                    mapTypeId === 'roadmap'
                      ? 'Vue satellite détaillée'
                      : 'Vue plan'
                  }
                >
                  {mapTypeId === 'roadmap' ? (
                    <SatelliteIcon className="h-5 w-5" />
                  ) : (
                    <MapIcon className="h-5 w-5" />
                  )}
                </button>
              </div>

              {!showList && programGroups.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowList(true)}
                  className="absolute bottom-4 left-3 z-10 inline-flex items-center gap-1.5 rounded-lg bg-white/95 px-3 py-2 text-xs font-semibold text-primary-600 shadow-md ring-1 ring-black/5 backdrop-blur-sm transition-colors hover:bg-white sm:left-4 sm:text-sm"
                >
                  <ListIcon className="h-4 w-4" />
                  Afficher la liste
                </button>
              )}

              {selectedProgramGroup && selectedProgramProject && (
                <MapSelectedProgramPanel
                  group={selectedProgramGroup}
                  project={selectedProgramProject}
                  onViewSheet={goToSelectedProgramPage}
                  onClose={clearProgramSelection}
                  showViewSheetButton={
                    !hideViewSheetForProgramKey ||
                    hideViewSheetForProgramKey !== selectedProgramKey
                  }
                />
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {placement === 'floating' &&
        (typeof document !== 'undefined'
          ? createPortal(floatingButton, document.body)
          : floatingButton)}
      {placement === 'inline' && triggerButton}
      {placement === 'overlay' && triggerButton}
      {embedMapBlock}
      {fullscreenOverlay}
    </>
  );
};

const MapWidget: React.FC<MapWidgetProps> = (props) => (
  <MapWidgetContent {...props} onEnableMaps={() => undefined} />
);

export default MapWidget;
